import fs from 'node:fs'
import os from 'node:os'
import path from 'node:path'

import { runVercel } from './runner.js'
import type { VercelRunner } from './types.js'

const ENV_PULL_TIMEOUT_MS = 60_000

/**
 * Pull the development environment into a temp dotenv file, hand it to
 * `read` to extract a value, then delete the temp file. Used after marketplace
 * provisioning (Neon, Resend) so BetterStart's `.env.local` is never
 * overwritten by `vercel env pull`.
 */
export async function pullVercelEnvValue<T>(
  runner: VercelRunner,
  cwd: string,
  read: (filePath: string) => T | undefined,
  env?: NodeJS.ProcessEnv
): Promise<T | undefined> {
  const tmpDir = fs.mkdtempSync(path.join(os.tmpdir(), 'betterstart-vercel-'))
  const tmpEnv = path.join(tmpDir, '.env.pull')

  try {
    const pull = await runVercel(
      runner,
      ['env', 'pull', tmpEnv, '--environment', 'development', '--yes'],
      { cwd, mode: 'capture', timeoutMs: ENV_PULL_TIMEOUT_MS, env }
    )
    if (!pull.success) return undefined
    return read(tmpEnv)
  } finally {
    try {
      fs.rmSync(tmpDir, { recursive: true, force: true })
    } catch {
      // best effort — never let temp cleanup abort provisioning
    }
  }
}

/** Parse a dotenv file into a key → unquoted-value map (empty when missing). */
export function parseDotenvFile(filePath: string): Map<string, string> {
  const vars = new Map<string, string>()
  if (!fs.existsSync(filePath)) return vars

  for (const line of fs.readFileSync(filePath, 'utf-8').split('\n')) {
    const trimmed = line.trim()
    if (trimmed.startsWith('#') || !trimmed.includes('=')) continue
    const eq = trimmed.indexOf('=')
    const key = trimmed.slice(0, eq).trim()
    const value = trimmed
      .slice(eq + 1)
      .replace(/^['"]|['"]$/g, '')
      .trim()
    if (key) vars.set(key, value)
  }

  return vars
}
